import * as moment from "moment";
import { createSelector } from "reselect";
import { values } from "lodash";
import { Appointment } from "@teamprevent/common/calendar";

import { ApplicationState } from "../applicationState";

import { ClinicDetails, UnknownEmployeeAppointment } from "./clinics/models";

const routeDateStringSelector = (state: ApplicationState) => {
    return (state.router.params || {}).date;
};

export const routeDateSelector = createSelector(routeDateStringSelector, (date: string) => {
    const dateAsMoment = date && moment.utc(date);
    if (dateAsMoment && dateAsMoment.isValid()) {
        return dateAsMoment;
    }

    return moment.utc();
});

const entityCalendarsSelector = (state: ApplicationState) => {
    return state.calendar;
};

export const clinicsSelector = (state: ApplicationState) => {
    return state.clinic.clinics;
};

const parentAppointmentsSelector = createSelector(
    entityCalendarsSelector,
    (entityCalendars) => {
        const calendars = values(entityCalendars);
        return calendars.reduce<(Appointment | UnknownEmployeeAppointment)[]>((a, b) => a.concat(b.appointments), []);
    });

const appointmentsSelector = createSelector(
    parentAppointmentsSelector,
    clinicsSelector,
    (parentAppointments: (Appointment | UnknownEmployeeAppointment)[], clinics: ClinicDetails[]) => {
        const childAppointments = parentAppointments.reduce<(Appointment | UnknownEmployeeAppointment)[]>((a, b) => a.concat(b.appointments), []);
        const clinicAppointments = clinics.reduce<(Appointment | UnknownEmployeeAppointment)[]>((a, b) => a.concat(b.appointments), []);
        return parentAppointments.concat(childAppointments).concat(clinicAppointments);
    });

const selectedAppointmentId = (state: ApplicationState) => {
    return state.router.params && state.router.params.appointmentId;
};

const selectedClinicId = (state: ApplicationState) => {
    return state.router.params && state.router.params.clinicId;
};

export const selectedAppointmentSelector = createSelector(
    appointmentsSelector,
    selectedAppointmentId,
    (appointments, appointmentId): (Appointment & { id?: string }) | undefined => {
        return appointments.find(a => a.id === appointmentId);
    }
);

export const selectAppointmentByIdSelector = (appointmentId: string | undefined) => createSelector(
    appointmentsSelector,
    (appointments) => {
        return appointmentId ? appointments.find(a => a.id === appointmentId) : undefined;
    }
);

export const selectParentAppointmentForAppointmentBySelector = (appointmentId: string | undefined) => createSelector(
    clinicsSelector,
    parentAppointmentsSelector,
    (clinics, parentAppointments): ClinicDetails | Appointment | undefined => {
        if (appointmentId === undefined) { return undefined; }

        return clinics.find(c => c.appointments.some(a => a.id === appointmentId))
        ?? parentAppointments.find(c => c.appointments.some(a => a.id === appointmentId))
        ?? parentAppointments.find(a => a.id === appointmentId);
    }
);

export const selectedClinicSelector = createSelector(
    clinicsSelector,
    selectedClinicId,
    (clinics, clinicId) => {
        return clinics.find(a => a.id === clinicId);
    }
);
